/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the student class page with its live banner and roster strip.
 * @state-owner dataset store
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks
 * @forbidden teacher-only controls; direct API client
 * @public-api workspace main column
 * @side-effects navigation only
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { GroupHeading, Page, PageBody, PageHeader } from '../../../shared/components/PageShell';
import { NameCell, ProgressBars, StatusText } from '../../../shared/components/StatusIcon';
import { Toolbar, tableCells, VTable } from '../../../shared/components/VTable';
import { ME, RUN_MODE_LABEL } from '../../../shared/lib/demo-data';
import { findActivity, isRunForMe, runName } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { GUARDIAN_STATE_LABEL, guardianState } from '../model/guardian';

export function StudentClassHome({ classIndex }: { classIndex: number }) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const guardians = useDatasetStore((state) => state.guardians[ME] ?? []);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const go = useWorkspaceUiStore((state) => state.go);
  const pick = useWorkspaceUiStore((state) => state.pick);
  const openItem = useWorkspaceUiStore((state) => state.openItem);

  const classroom = classes[classIndex];
  const mine = runs.filter((run) => run.classIndex === classIndex && isRunForMe(classes, run));
  const current = mine.filter((run) => run.status === 'live' || run.status === 'open');
  const finished = mine.filter((run) => run.status === 'ended');
  const live = mine.find((run) => run.status === 'live');
  const guardian = GUARDIAN_STATE_LABEL[guardianState(guardians)];
  const linked = guardianState(guardians) === 'linked';

  return (
    <Page>
      <PageHeader
        actions={
          live ? (
            <button className="bt pri" type="button" onClick={() => navigate('act', live.id)}>
              {t('進去上課')}
            </button>
          ) : null
        }
        meta={
          <>
            {`${classroom.subtitle} · ${t('同班')} ${classroom.students.length} ${t('人')}`}
            {live ? <em className="hot">{` · ${t('上課中')}`}</em> : null}
          </>
        }
        title={classroom.name}
      />
      <Toolbar
        left={
          <span className="vsumline">
            <b>{current.length}</b> {t('項待完成')} <em>·</em> <b>{finished.length}</b>{' '}
            {t('項已完成')} <em>·</em> AI 陪你 <b>4</b> 次 <em>·</em> 老師來幫你 <b>1</b> 次
          </span>
        }
      />
      <PageBody>
        {live ? (
          <div className="livebar" style={{ margin: '16px 0 18px' }}>
            <span className="dot2" />
            <span className="lt">
              <b>{t('老師正在上課')}</b>
              <span>
                {`${runName(activities, live)} · ${findActivity(activities, live.activityId)?.questionCount ?? 0} ${t('題')}`}
              </span>
            </span>
            <button className="bt pri" type="button" onClick={() => navigate('act', live.id)}>
              {t('跟上進度')}
            </button>
          </div>
        ) : null}

        <VTable
          empty={t('這個班還沒有指派東西給你。')}
          head={[t('活動'), t('方式'), t('狀態'), t('時間'), t('進度')]}
          rows={mine.map((run) => ({
            key: run.id,
            dim: run.status === 'ended',
            onSelect: () => {
              go(run.status === 'ended' ? 'rep' : 'act', null);
              pick(run.id);
            },
            cells: tableCells(
              <NameCell
                icon={run.mode === 'live' ? '課' : t('作')}
                text={runName(activities, run)}
                tone={run.status === 'live' ? 'p' : ''}
              />,
              <span className="vmut">{t(RUN_MODE_LABEL[run.mode])}</span>,
              run.status === 'live' ? (
                <StatusText glyph="live" label={t('進行中')} tone="pr" />
              ) : run.status === 'open' ? (
                <StatusText glyph="warn" label={t('還沒交')} tone="wn" />
              ) : (
                <StatusText glyph="done" label={t('已完成')} />
              ),
              <span className="vmut">{t(run.dateLabel)}</span>,
              run.status === 'ended' ? (
                <span className="vmut">92 分</span>
              ) : (
                <ProgressBars level={run.status === 'live' ? 2 : 1} />
              ),
            ),
          }))}
          template="minmax(0,1fr) 100px 130px 110px 60px"
        />

        <button className="ginv" type="button" onClick={() => openItem({ k: 'minv' })}>
          <span className="gav">{t('家')}</span>
          <span className="gt">
            <b>{t('邀請我的家長')}</b>
            <span>{linked ? t('已經綁定好了') : t('家長綁定之後才收得到你的報告')}</span>
          </span>
          <StatusText
            glyph={linked ? 'done' : guardian[1]}
            label={linked ? t('已綁定') : t('還沒綁定')}
            tone={linked ? '' : 'wn'}
          />
        </button>

        <GroupHeading count={classroom.students.length}>{t('同班同學')}</GroupHeading>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, padding: '2px 8px 8px' }}>
          {classroom.students.map((student) => (
            <span className="vcls" key={student} title={student}>
              {student[0]}
            </span>
          ))}
        </div>
        <p style={{ fontSize: 12, color: 'var(--ln-mut)', padding: '0 8px' }}>
          {t('只看得到名字，看不到別人的成績。')}
        </p>
      </PageBody>
    </Page>
  );
}
